/* eslint-disable max-len */
import { Add } from '@mui/icons-material';
import { Button, Grid, Stack, TextField, TextareaAutosize, Typography } from '@mui/material';
import React, { useContext, useState } from 'react';
import { LanguageContext } from '../../../../../../../libs/Context';
import CreateQuestions from './Questions';
import { createSurvey } from '../../../../../../actions/meeting_actions';
import { toast } from 'react-toastify';
import { catchErr } from '../../../../../../../libs/HelperContent';
import Loading from '../../../../../../../components/Common/Loaders/FullWidthLoader/Loading';

const CreateSurvey = (props) =>
{
	const { initialState, handleAction, form, setInitialState, editId } = props;
	const { language } = useContext(LanguageContext);

	const [ data, setData ] = useState({ title: initialState?.title ?? '', description: initialState?.description ?? '' });
	const [ loading, setLoading ] = useState(false);

	const initialQue = {
		questionType : 'short',
		question     : '',
		options      : [],
		questionId   : `id-${Date.now()}-${Math.floor(Math.random() * 10000)}`
	};

	const [ questionD, setQuestionD ] = useState(initialState?.questions?.length > 0 ? initialState?.questions : [ initialQue ]);

	const getQuestions = () =>
	{
		return questionD.map(({ questionType, question, options, questionId }) =>
		{
			const result:any = {
				questionType,
				question,
				questionId
			};

			if ([ 'singleChoice', 'multipleChoice' ].includes(questionType))
			{
				result.options = options?.filter((option:any) => option.trim());
			}

			return result;
		});
	};

	const isAnyFieldEmpty = () =>
	{
		return questionD.some((q) =>
			!q?.question || !q?.questionType ||
				([ 'singleChoice', 'multipleChoice' ].includes(q?.questionType) && q?.options?.some((opt:any) => !opt?.trim()))
		);
	};

	const handleClose = () =>
	{
		handleAction({ edit: '', openCreate: false });
	};

	const createSurveyApi = async () =>
	{
		try
		{
			setLoading(true);
			const questionList = getQuestions();
			const body = {
				sessionId   : form?.id,
				title       : data?.title,
				description : data?.description,
				questions   : questionList
			};

			const res = await createSurvey(body);

			if (res?.status)
			{
				const isEdit = Boolean(editId) || Boolean(initialState?.title);

				toast.success(
					`${language?.survey} ${
						isEdit ? language?.updated : language?.created
					} ${language?.sucess}`,
					{
						position  : toast.POSITION.TOP_RIGHT,
						className : 'toast-message'
					});
				setInitialState((prev) => ({
					...prev,
					title       : data?.title,
					description : data?.description,
					questions   : questionList
				}));
				handleClose();
			}
		}
		catch (err)
		{
			catchErr(err);
		}
		setLoading(false);
	};

	const disableCreate = () =>
	{
		if (questionD?.length > 0)
		{
			const isTitleSame = JSON.stringify(data?.title) === JSON.stringify(initialState?.title);
			const isDescriptionSame = JSON.stringify(data?.description) === JSON.stringify(initialState?.description);
			const isQuestionListSame = JSON?.stringify(initialState?.questions) === JSON?.stringify(questionD);

			return !data?.title || (isTitleSame && isDescriptionSame && isQuestionListSame) || isAnyFieldEmpty();
		}
		else
		{
			return questionD.every((q) =>
				q.question &&
                q.questionType &&
                !((q.questionType === 'singleChoice' || q.questionType === 'multipleChoice') && q.options?.some((opt) => !opt?.value?.trim()))
			) && initialState.title && initialState.description;
		}
	};

	const handleChange = (key, value) =>
	{
		setData({ ...data, [key]: value.replace(/\s+/g, ' ') });
	};

	const handleDeleteQuestion = (ind) =>
	{
		const questionList = [ ...questionD ];

		questionList.splice(ind, 1);

		setQuestionD(questionList);
	};

	return (
		<Grid item xs={12} px='var(--pixel16)'>
			{loading && <Loading/>}
			<Grid container flexDirection='column' px='var(--pixel8)'>
				<Grid item sm={12}>
					<Stack direction='column'>
						<Typography sx={{ paddingBottom: 'var(--pixel4)' }} variant='subtitle2'>
							{`${language.title} *`}
						</Typography>
						<TextField
							autoFocus={data?.title === ''}
							placeholder={language.entertitle}
							value={data?.title || ''}
							onChange={(e) => handleChange('title', e.target.value)}
							id={language?.enterTit}
							size='small'
							inputProps={{ maxLength: 100 }}
						/>
					</Stack>
					<Typography textAlign='end' variant='body2'>
						{`${data?.title?.length ?? 0} / 100`}
					</Typography>
				</Grid>
				<Grid item sm={12}>
					<Stack direction='column'>
						<Typography sx={{ paddingBottom: 'var(--pixel4)' }} variant='subtitle2'>
							{language.description}
						</Typography>
						<TextareaAutosize
							aria-label='minimum height'
							minRows={3}
							value={data?.description || ''}
							onChange={(e) => handleChange('description', e.target.value.slice(0, 300))}
							placeholder={language.enterDescript}
							// className='lightTxt'
							style={{ width: '100%', padding: 'var(--pixel4)', borderRadius: 'var(--pixel4)' }}
							name='description'
						/>
						<Typography textAlign='end' variant='body2'>
							{`${data?.description?.length ?? 0} / 300`}
						</Typography>
					</Stack>
				</Grid>
			</Grid>
			<Grid container xs={12} flexDirection='column'>
				<Grid item xs={12} p='var(--pixel8)'>
					<Typography variant='subtitle1' fontWeight='var(--font500)'>
						{language.questions} ({questionD?.length})
					</Typography>
					<Grid container xs={12} flexDirection='column'>
						<Stack direction='column' sx={{ maxHeight: '400px', overflowY: 'auto' }}>
							{questionD.map((questn, index) => (
								<CreateQuestions
									key={questn?.questionId}
									editId={editId}
									showDelete={questionD.length > 1}
									question={questn}
									index={index}
									setQuestionD={setQuestionD}
									handleDeleteQuestion={handleDeleteQuestion}
									title={data?.title}
								/>
							))}
						</Stack>
					</Grid>
					<Stack direction='column' width='var(--percent100)' pt='var(--pixel16)'>
						<Button
							variant='outlined'
							sx={{ textTransform: 'capitalize' }}
							disabled={isAnyFieldEmpty()}
							onClick={() => setQuestionD((prev) => ([ ...prev, { ...initialQue } ]))}
						>
							<Add color='primary' />&nbsp;{language.addMore}
						</Button>
					</Stack>
				</Grid>
			</Grid>
			<Stack direction='row' p='var(--pixel16)' alignItems='center' justifyContent='flex-end'>
				<Button
					disableElevation
					disableFocusRipple
					disableRipple
					disableTouchRipple
					variant='outlined'
					sx={{ textTransform: 'capitalize' }}
					onClick={handleClose}
				>
					{language.cancel}
				</Button>
				<Typography pl='var(--pixel16)'>
					<Button
						disableElevation
						disableFocusRipple
						disableRipple
						disableTouchRipple
						variant='contained'
						sx={{ color: 'var(--whiteColor)', textTransform: 'capitalize' }}
						onClick={createSurveyApi}
						disabled={disableCreate()}
					>
						{initialState?.title ? language?.updateSurvey : language?.createSurvey}
					</Button>
				</Typography>
			</Stack>
		</Grid>
	);
};

export default CreateSurvey;
